
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Define o objeto fbq para evitar erros de TypeScript
declare global {
  interface Window {
    fbq: (
      eventType: string,
      eventName: string,
      params?: any
    ) => void;
  }
}

export type MetaPixelEventParams = {
  content_name?: string;
  content_category?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
  predicted_ltv?: number;
  [key: string]: any;
};

export function useMetaPixel() {
  const location = useLocation();

  // Verifica se o usuário deu consentimento para cookies
  const hasConsent = () => {
    return localStorage.getItem('cookie-consent') === 'accepted';
  };

  // Rastrear mudanças de página
  useEffect(() => {
    if (typeof window.fbq !== 'undefined' && hasConsent()) {
      window.fbq('track', 'PageView');
      console.log('📊 Meta Pixel: Page view', location.pathname);
    }
  }, [location]);

  // Função para enviar eventos personalizados
  const sendEvent = (
    eventName: string,
    params?: MetaPixelEventParams
  ) => {
    // Sempre envia eventos relacionados ao consentimento, mesmo sem consentimento prévio
    const isConsentRelated = eventName.includes('Consent') || 
                           (params?.content_category && params.content_category.includes('consent'));
    
    if (typeof window.fbq !== 'undefined' && (hasConsent() || isConsentRelated)) {
      window.fbq('track', eventName, params);
      console.log('📊 Meta Pixel: Event', eventName, params);
    } else {
      console.log('📊 Meta Pixel: Event não enviado (sem consentimento)', eventName);
    }
  };

  // Função específica para eventos de purchase
  const sendPurchaseEvent = (planType: string, value: number, currency: string = 'EUR') => {
    sendEvent('Purchase', {
      content_name: `${planType} Subscription`,
      content_category: 'subscription',
      content_ids: [`subscription_${planType.toLowerCase()}`],
      content_type: 'subscription',
      value: value,
      currency: currency,
      predicted_ltv: value * 12 // Valor anual estimado
    });
  };

  // Função para eventos de checkout iniciado
  const sendInitiateCheckoutEvent = (planType: string, value: number, currency: string = 'EUR') => {
    sendEvent('InitiateCheckout', {
      content_name: `${planType} Subscription`,
      content_category: 'subscription',
      content_ids: [`subscription_${planType.toLowerCase()}`],
      content_type: 'subscription',
      value: value,
      currency: currency
    });
  };

  // Função para eventos de trial iniciado
  const sendStartTrialEvent = (planType: string = 'Premium') => {
    sendEvent('StartTrial', {
      content_name: `${planType} Trial`,
      content_category: 'trial',
      content_ids: [`trial_${planType.toLowerCase()}`],
      content_type: 'subscription',
      value: 0,
      currency: 'EUR'
    });
  };

  // Função para eventos de lead
  const sendLeadEvent = (leadType: string, value?: number) => {
    sendEvent('Lead', {
      content_name: leadType,
      content_category: 'lead',
      content_type: 'lead',
      value: value || 0,
      currency: 'EUR'
    });
  };

  // Função para view content
  const sendViewContentEvent = (contentName: string, contentCategory: string, value?: number) => {
    sendEvent('ViewContent', {
      content_name: contentName,
      content_category: contentCategory,
      content_type: contentCategory,
      value: value || 0,
      currency: 'EUR'
    });
  };

  // Função para eventos personalizados de subscription
  const sendSubscriptionUpgradeEvent = (fromPlan: string, toPlan: string, value: number) => {
    sendEvent('SubscriptionUpgrade', {
      content_name: `Upgrade from ${fromPlan} to ${toPlan}`,
      content_category: 'subscription_upgrade',
      content_ids: [`upgrade_${fromPlan}_to_${toPlan}`],
      content_type: 'subscription',
      value: value,
      currency: 'EUR'
    });
  };

  const sendPlanChangeEvent = (fromPlan: string, toPlan: string) => {
    sendEvent('PlanChange', {
      content_name: `Plan Change: ${fromPlan} to ${toPlan}`,
      content_category: 'plan_change',
      content_ids: [`change_${fromPlan}_to_${toPlan}`],
      content_type: 'subscription'
    });
  };

  const sendSubscriptionRenewalEvent = (planType: string, value: number) => {
    sendEvent('SubscriptionRenewal', {
      content_name: `${planType} Renewal`,
      content_category: 'subscription_renewal',
      content_ids: [`renewal_${planType.toLowerCase()}`],
      content_type: 'subscription',
      value: value,
      currency: 'EUR'
    });
  };

  return { 
    sendEvent, 
    hasConsent,
    sendPurchaseEvent,
    sendInitiateCheckoutEvent,
    sendStartTrialEvent,
    sendLeadEvent,
    sendViewContentEvent,
    sendSubscriptionUpgradeEvent,
    sendPlanChangeEvent,
    sendSubscriptionRenewalEvent
  };
}

// Eventos comuns pré-definidos
export const MetaPixelEvents = {
  // Eventos de usuário
  COMPLETE_REGISTRATION: 'CompleteRegistration',
  CONTACT: 'Contact',
  
  // Eventos de assinatura
  START_TRIAL: 'StartTrial',
  SUBSCRIBE: 'Subscribe',
  
  // Eventos de conversão
  PURCHASE: 'Purchase',
  LEAD: 'Lead',
  INITIATE_CHECKOUT: 'InitiateCheckout',
  VIEW_CONTENT: 'ViewContent',
  
  // Eventos de cookies
  CONSENT_ACCEPTED: 'ConsentAccepted',
  CONSENT_DECLINED: 'ConsentDeclined',
  
  // Eventos personalizados de subscription
  SUBSCRIPTION_UPGRADE: 'SubscriptionUpgrade',
  PLAN_CHANGE: 'PlanChange',
  SUBSCRIPTION_RENEWAL: 'SubscriptionRenewal',
  
  // Outros eventos padrão do Facebook
  ADD_TO_CART: 'AddToCart',
  ADD_TO_WISHLIST: 'AddToWishlist',
  SEARCH: 'Search',
  CUSTOM_EVENT: 'CustomEvent'
};
